/**
 * DebriefingResponseModal — modale apprenti pour répondre à un débriefing.
 *
 * 11/02/2026 (Phase 3 v2 — Superpouvoirs formateur).
 *
 * Rendu conditionnel : visible s'il existe au moins 1 débriefing en attente
 * dans `state.pendingDebriefings`. L'apprenti peut :
 *   - Répondre à toutes les questions (text / number / choice)
 *   - Soumettre (POST /api/master/debriefings/{id}/respond)
 *   - Fermer sans envoyer (garde le débriefing pending pour plus tard)
 *
 * Après un submit réussi, le débriefing sort de la file locale.
 */
import React, { useState } from 'react';
import { ClipboardList, X, Send, Loader2, Check } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import useLicenseStore from '../../store/licenseStore';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function DebriefingResponseModal() {
  const pending = useGameStore((s) => s.pendingDebriefings) || [];
  const licenseKey = useLicenseStore((s) => s.licenseKey);
  const [visible, setVisible] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const current = pending[activeIdx];

  const setAnswer = (qid, val) => setAnswers((a) => ({ ...a, [qid]: val }));

  const closeModal = () => {
    setVisible(false);
    setAnswers({});
    setSubmitted(false);
    setError(null);
  };

  const submit = async () => {
    if (!current) return;
    setSubmitting(true);
    setError(null);
    try {
      const resp = await fetch(`${BACKEND}/api/master/debriefings/${current.id}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ license_key: licenseKey, answers }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      // Retire de la file locale
      useGameStore.setState({
        pendingDebriefings: pending.filter((d) => d.id !== current.id),
      });
      setSubmitted(true);
      setTimeout(() => closeModal(), 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Rien à afficher si aucun débriefing en attente et modale fermée
  if (pending.length === 0) return null;

  return (
    <>
      {/* Bouton flottant qui invite à ouvrir la modale */}
      {!visible && (
        <button
          onClick={() => { setVisible(true); setActiveIdx(0); setAnswers({}); setSubmitted(false); setError(null); }}
          className="fixed bottom-24 left-6 z-[9990] bg-indigo-500 hover:bg-indigo-500/80 text-white rounded-full px-3 py-2 shadow-lg flex items-center gap-2 text-xs font-bold border border-indigo-300"
          data-testid="debriefing-open-button"
          title={`${pending.length} débriefing${pending.length > 1 ? 's' : ''} en attente`}
        >
          <ClipboardList className="w-4 h-4" />
          Débriefing ({pending.length})
        </button>
      )}
      {visible && current && (
        <div className="fixed inset-0 z-[9995] bg-ink-950/80 flex items-center justify-center p-4" data-testid="debriefing-response-modal">
          <div className="bg-ink-800 border border-ink-600 rounded-lg max-w-lg w-full max-h-[80vh] overflow-y-auto">
            <header className="px-4 py-3 border-b border-ink-600 flex items-center justify-between sticky top-0 bg-ink-800 z-10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-indigo-300 font-bold">
                  Débriefing · {activeIdx + 1} / {pending.length}
                </div>
                <h3 className="text-sm font-bold text-foreground">{current.title}</h3>
                <div className="text-[11px] text-slate-500">de {current.master_holder}</div>
              </div>
              <button onClick={closeModal} className="text-slate-400 hover:text-red-400" data-testid="debriefing-modal-close">
                <X className="w-4 h-4" />
              </button>
            </header>
            <div className="p-4 space-y-3">
              {submitted && (
                <div className="bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 rounded p-2 text-xs flex items-center gap-2" data-testid="debriefing-response-success">
                  <Check className="w-4 h-4" /> Merci ! Vos réponses ont été envoyées.
                </div>
              )}
              {current.intro && !submitted && (
                <p className="text-xs text-slate-400 italic">{current.intro}</p>
              )}
              {!submitted && current.questions.map((q, i) => (
                <div key={q.id} className="space-y-1" data-testid={`debriefing-response-q-${i}`}>
                  <label className="text-xs font-bold text-foreground">{i + 1}. {q.text}</label>
                  {q.kind === 'text' && (
                    <textarea
                      value={answers[q.id] || ''}
                      onChange={(e) => setAnswer(q.id, e.target.value)}
                      maxLength={800}
                      className="w-full bg-ink-950 border border-ink-600 rounded p-2 text-xs text-slate-200 h-16 resize-none focus:outline-none focus:border-accent"
                    />
                  )}
                  {q.kind === 'number' && (
                    <input
                      type="number"
                      value={answers[q.id] ?? ''}
                      onChange={(e) => setAnswer(q.id, e.target.value === '' ? '' : parseFloat(e.target.value))}
                      className="w-full bg-ink-950 border border-ink-600 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-accent"
                    />
                  )}
                  {q.kind === 'choice' && (
                    <div className="space-y-1">
                      {(q.choices || []).map((c) => (
                        <label key={c} className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                          <input
                            type="radio"
                            name={`q-${q.id}`}
                            value={c}
                            checked={answers[q.id] === c}
                            onChange={() => setAnswer(q.id, c)}
                            className="accent-indigo-500"
                          />
                          {c}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {error && (
                <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/40 rounded p-2" data-testid="debriefing-response-error">
                  {error}
                </div>
              )}
              {!submitted && (
                <div className="flex items-center justify-between pt-2">
                  <span className="text-[10px] text-slate-500">Vous pouvez modifier après envoi.</span>
                  <button
                    onClick={submit}
                    disabled={submitting || Object.keys(answers).length === 0}
                    className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded bg-indigo-500 text-white hover:bg-indigo-500/80 disabled:opacity-40"
                    data-testid="debriefing-response-submit"
                  >
                    {submitting && <Loader2 className="w-3 h-3 animate-spin" />}
                    <Send className="w-3 h-3" />
                    Envoyer mes réponses
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
